/* ═══════════════════════════════════════════════════════════════
   TaskLinks — Task Sheet's Links section. Read for anyone who can see
   the task; add/remove gated on the task's own edit right (Decision 12).
   ═══════════════════════════════════════════════════════════════ */
import { useState } from 'react';
import type { TaskLink } from '../../../../shared/types';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Icon } from '../../../components/ui/Icon';
import EmptyState from '../../../components/EmptyState';
import { rem } from '../../../lib/rem';
import { X, Link as LinkIcon } from 'lucide-react';

export interface TaskLinksProps {
  links: TaskLink[];
  canEdit: boolean;
  onAdd: (url: string, label: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const sectionHeaderStyle = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  color: 'var(--tm)',
  textTransform: 'uppercase' as const,
  letterSpacing: '.05em',
};

export function TaskLinks({ links, canEdit, onAdd, onDelete }: TaskLinksProps) {
  const [showAddLink, setShowAddLink] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkLabel, setNewLinkLabel] = useState('');

  async function handleAddLink() {
    if (!newLinkUrl.trim()) return;
    await onAdd(newLinkUrl, newLinkLabel);
    setNewLinkUrl('');
    setNewLinkLabel('');
    setShowAddLink(false);
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: rem(6),
        }}
      >
        <span style={sectionHeaderStyle}>Links {links.length > 0 ? `(${links.length})` : ''}</span>
        {canEdit && (
          <Button variant="ghost" size="sm" onClick={() => setShowAddLink(true)}>
            + Add
          </Button>
        )}
      </div>

      {links.length === 0 && !showAddLink && (
        <EmptyState compact icon={<Icon icon={LinkIcon} size="md" />} title="No links" />
      )}

      {links.map((lnk) => (
        <div
          key={lnk.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: `var(--space-1) 0`,
            borderBottom: '1px solid var(--bd)',
          }}
        >
          <a
            href={lnk.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              fontSize: 'var(--font-size-base)',
              color: 'var(--ac)',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textDecoration: 'underline',
            }}
          >
            {lnk.label || lnk.url}
          </a>
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(lnk.id)}
              aria-label="Remove link"
            >
              <Icon icon={X} size="md" />
            </Button>
          )}
        </div>
      ))}

      {showAddLink && canEdit && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
            marginTop: rem(6),
          }}
        >
          <Input
            value={newLinkUrl}
            onChange={(e) => setNewLinkUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddLink()}
            placeholder="https://..."
            autoFocus
          />
          <Input
            value={newLinkLabel}
            onChange={(e) => setNewLinkLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddLink()}
            placeholder="Label (optional)"
          />
          <div style={{ display: 'flex', gap: rem(6) }}>
            <Button variant="secondary" size="sm" onClick={handleAddLink}>
              Add
            </Button>
            <Button
              variant="outline"
              size="sm"
              aria-label="Cancel"
              onClick={() => {
                setShowAddLink(false);
                setNewLinkUrl('');
                setNewLinkLabel('');
              }}
            >
              <Icon icon={X} em={1.4} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
